import { useEffect, useState } from "react";
import { onAuthStateChanged } from "firebase/auth";
import { auth } from "@/services/firebase/init";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArtistLink,
  ArtistProfile,
  artistProfileAudit,
  artistProfileGet,
  artistProfileIdentityGet,
  artistProfileUpdate,
  creatorSetHandle,
} from "@/services/firebase/artistHub";
import { HANDLE_RULE_TEXT, handleProblem, handleShareUrl, normalizeHandle } from "@/lib/creator";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const auditLabels: Record<string, string> = {
  profileUpdate: "修改專頁資料",
  contentPublish: "發布內容",
  contentUnpublish: "下架內容",
  contentDelete: "刪除內容",
  saveDraft: "儲存草稿",
  submit: "送審",
  return: "退回修改",
  grant: "授權成員",
  revoke: "撤銷成員",
  entitlementGrant: "發放鐵粉權益",
  entitlementRevoke: "撤銷鐵粉權益",
  contentSetAudience: "鐵粉內容改為公開",
  creatorSetHandle: "設定 @handle",
  featuredOrderSet: "調整精選排序",
};
const fieldLabels: Record<string, string> = {
  displayName: "暱稱",
  legalName: "本名（私有驗證資料）",
  bio: "簡介",
  links: "連結",
  avatarUrl: "頭像",
  coverUrl: "封面",
  handle: "@handle",
};
const typeLabels: Record<ArtistLink["type"], string> = {
  instagram: "Instagram",
  youtube: "YouTube",
  threads: "Threads",
  facebook: "Facebook",
  other: "其他",
};
type LinkDraft = { url: string; label: string };
const linkDrafts = (links: ArtistLink[]): LinkDraft[] => links.map((l) => ({ url: l.url, label: l.label }));

/**
 * B-06: shows the self-service profile fields the artist edited in the App (displayName / bio /
 * links / avatar / cover, with counters) plus the audit trail, and lets staff overwrite the text
 * fields through the same profileUpdate action. Avatar/cover are uploaded from the App.
 */
export default function ArtistProfilePanel({ artistId }: { artistId: string }) {
  const client = useQueryClient();
  const profileKey = ["artist-profile", artistId];
  const auditKey = ["artist-audit", artistId];
  const profile = useQuery({ queryKey: profileKey, queryFn: () => artistProfileGet(artistId), retry: false });
  const [identityUid, setIdentityUid] = useState<string | null>(null);
  useEffect(() => onAuthStateChanged(auth, (user) => setIdentityUid(user?.uid ?? null)), []);
  const identity = useQuery({
    queryKey: ["artist-private-identity", artistId, identityUid],
    enabled: !!identityUid,
    queryFn: async () => {
      if (!identityUid || auth.currentUser?.uid !== identityUid) throw new Error("帳號已變更");
      const result = await artistProfileIdentityGet(artistId);
      if (auth.currentUser?.uid !== identityUid) throw new Error("帳號已變更");
      return result;
    },
    retry: false,
    gcTime: 0,
  });
  const audit = useQuery({ queryKey: auditKey, queryFn: () => artistProfileAudit(artistId, 20), retry: false });
  const [editing, setEditing] = useState(false);
  const [displayName, setDisplayName] = useState("");
  const [bio, setBio] = useState("");
  const [links, setLinks] = useState<LinkDraft[]>([]);
  useEffect(() => {
    if (profile.data) {
      setDisplayName(profile.data.displayName);
      setBio(profile.data.bio);
      setLinks(linkDrafts(profile.data.links));
    }
  }, [profile.data]);
  const update = useMutation({
    mutationFn: () =>
      artistProfileUpdate(artistId, {
        displayName: displayName.trim(),
        bio: bio.trim(),
        links: links.filter((l) => l.url.trim()).map((l) => ({ url: l.url.trim(), label: l.label.trim() })),
      }),
    onSuccess: (saved: ArtistProfile) => {
      client.setQueryData(profileKey, saved);
      void client.invalidateQueries({ queryKey: auditKey });
      setEditing(false);
    },
  });
  const data = profile.data;
  return (
    <Card className="mb-6">
      <CardHeader>
        <CardTitle>App 專頁資料（藝人自助編輯）</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          藝人可在 App 直接修改公開暱稱、簡介、連結與頭像／封面，立即生效。本名另存私有驗證資料，不會公開。此處顯示 App 目前的值，工作人員可覆寫文字欄位；「同步已儲存資料」會以上方演員資料覆蓋公開暱稱與簡介。
        </p>
        <div className="rounded border p-3 text-sm" aria-label="私有驗證資料">
          <p className="font-medium">本名（僅本人與營運審核可見）</p>
          <p>{identity.isPending ? "載入中…" : identity.isError ? "無法讀取私有驗證資料" : identity.data?.legalName || "尚未提供"}</p>
        </div>
        {profile.isPending && <p>讀取專頁資料…</p>}
        {profile.isError && (
          <p role="alert" className="text-destructive">
            無法讀取專頁資料：{profile.error.message}
          </p>
        )}
        {profile.isSuccess && !data && <p>專頁尚未發布，發布後才會有 App 專頁資料。</p>}
        {data && !editing && (
          <div className="space-y-2">
            <div className="flex flex-wrap items-center gap-4">
              {data.avatarUrl ? (
                <img src={data.avatarUrl} alt="頭像" className="h-16 w-16 rounded-full object-cover" />
              ) : (
                <span className="text-sm text-muted-foreground">尚未設定頭像</span>
              )}
              {data.coverUrl ? (
                <img src={data.coverUrl} alt="封面" className="h-16 w-28 rounded object-cover" />
              ) : (
                <span className="text-sm text-muted-foreground">尚未設定封面</span>
              )}
            </div>
            <p>
              <strong className="break-words">{data.displayName}</strong>
              <span className="ml-3 text-sm text-muted-foreground">
                追蹤 {data.followerCount} · 圖文 {data.postCount} · Vlog {data.vlogCount}
              </span>
            </p>
            <p className="whitespace-pre-wrap break-words text-sm">{data.bio || "（尚未填寫簡介）"}</p>
            {data.links.length > 0 ? (
              <ul className="space-y-1 text-sm">
                {data.links.map((l) => (
                  <li key={l.url} className="break-all">
                    {typeLabels[l.type]}
                    {l.label ? `（${l.label}）` : ""}：
                    <a href={l.url} target="_blank" rel="noreferrer" className="underline">
                      {l.url}
                    </a>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-muted-foreground">（尚未設定連結）</p>
            )}
            <Button variant="outline" onClick={() => setEditing(true)}>
              覆寫文字欄位
            </Button>
          </div>
        )}
        {data && editing && (
          <div className="space-y-3">
            <div>
              <Label htmlFor="artist-profile-name">暱稱（最多 30 字）</Label>
              <Input
                id="artist-profile-name"
                value={displayName}
                maxLength={30}
                disabled={update.isPending}
                onChange={(e) => setDisplayName(e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="artist-profile-bio">簡介（最多 150 字）</Label>
              <Textarea
                id="artist-profile-bio"
                rows={4}
                value={bio}
                maxLength={150}
                disabled={update.isPending}
                onChange={(e) => setBio(e.target.value)}
              />
              <p className="text-sm text-muted-foreground">{[...bio].length} / 150</p>
            </div>
            <div className="space-y-2">
              <Label>連結（最多 3 個，必須以 https:// 開頭；類型依網域自動判斷）</Label>
              {links.map((l, i) => (
                <div key={i} className="flex flex-wrap gap-2">
                  <Input
                    aria-label={`連結 ${i + 1} 網址`}
                    className="min-w-[16rem] flex-1"
                    placeholder="https://www.instagram.com/…"
                    value={l.url}
                    disabled={update.isPending}
                    onChange={(e) => setLinks(links.map((x, n) => (n === i ? { ...x, url: e.target.value } : x)))}
                  />
                  <Input
                    aria-label={`連結 ${i + 1} 標籤`}
                    className="w-40"
                    placeholder="標籤（選填）"
                    value={l.label}
                    maxLength={30}
                    disabled={update.isPending}
                    onChange={(e) => setLinks(links.map((x, n) => (n === i ? { ...x, label: e.target.value } : x)))}
                  />
                  <Button variant="outline" disabled={update.isPending} onClick={() => setLinks(links.filter((_, n) => n !== i))}>
                    移除
                  </Button>
                </div>
              ))}
              {links.length < 3 && (
                <Button variant="outline" disabled={update.isPending} onClick={() => setLinks([...links, { url: "", label: "" }])}>
                  新增連結
                </Button>
              )}
            </div>
            {update.error && (
              <p role="alert" className="text-destructive">
                {update.error.message}
              </p>
            )}
            <div className="flex flex-wrap gap-2">
              <Button disabled={update.isPending || !displayName.trim()} onClick={() => update.mutate()}>
                {update.isPending ? "儲存中…" : "覆寫並立即生效"}
              </Button>
              <Button
                variant="outline"
                disabled={update.isPending}
                onClick={() => {
                  setEditing(false);
                  setDisplayName(data.displayName);
                  setBio(data.bio);
                  setLinks(linkDrafts(data.links));
                }}
              >
                取消
              </Button>
            </div>
          </div>
        )}
        {update.isSuccess && !editing && <p role="status">專頁資料已覆寫。</p>}
        {data && <HandleSection artistId={artistId} handle={data.handle ?? null} />}
        <details className="border-t pt-3">
          <summary>稽核紀錄（最近 20 筆）</summary>
          {audit.isPending && <p className="text-sm">讀取中…</p>}
          {audit.isError && <p className="text-sm text-destructive">無法讀取稽核紀錄：{audit.error.message}</p>}
          {audit.data && audit.data.length === 0 && <p className="text-sm">尚無紀錄。</p>}
          {audit.data && audit.data.length > 0 && (
            <ul className="mt-2 space-y-1 text-sm">
              {audit.data.map((entry) => (
                <li key={entry.id} className="break-all">
                  {entry.at ? new Date(entry.at).toLocaleString("zh-TW") : "—"} · {auditLabels[entry.action] ?? entry.action}
                  {entry.fields?.length ? `（${entry.fields.map((f) => fieldLabels[f] ?? f).join("、")}）` : ""} · {entry.role ?? "?"} · {entry.actorUid}
                  {entry.target && entry.target !== artistId ? ` · ${entry.target}` : ""}
                </li>
              ))}
            </ul>
          )}
          <Button variant="outline" className="mt-2" disabled={audit.isFetching} onClick={() => void audit.refetch()}>
            重新整理
          </Button>
        </details>
      </CardContent>
    </Card>
  );
}

/**
 * C-05: @handle（contract v2.7 creatorSetHandle）. Artists can set it once in the App; staff may change it any time.
 * Format `^[a-z0-9._]{3,20}$`; 409 HANDLE_TAKEN when another artist owns it. Share link: on-reel.com/a/{handle}.
 */
function HandleSection({ artistId, handle }: { artistId: string; handle: string | null }) {
  const client = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(handle ?? "");
  const [touched, setTouched] = useState(false);
  const [copied, setCopied] = useState(false);
  const problem = handleProblem(value);
  const save = useMutation({
    mutationFn: () => creatorSetHandle(artistId, normalizeHandle(value)),
    onSuccess: (saved) => {
      client.setQueryData<ArtistProfile | null>(["artist-profile", artistId], (prev) => (prev ? { ...prev, handle: saved.handle } : prev));
      void client.invalidateQueries({ queryKey: ["artist-audit", artistId] });
      setEditing(false);
      setTouched(false);
    },
  });
  const shareUrl = handle ? handleShareUrl(handle) : null;
  return (
    <section className="space-y-2 border-t pt-3" aria-label="@handle">
      <h3 className="font-medium">@handle 與分享連結</h3>
      {!editing ? (
        <div className="space-y-2 text-sm">
          <p>
            目前：{handle ? <strong className="break-all">@{handle}</strong> : <span className="text-muted-foreground">尚未設定</span>}
          </p>
          {shareUrl && (
            <p className="flex flex-wrap items-center gap-2 break-all">
              分享連結：
              <a href={shareUrl} target="_blank" rel="noreferrer" className="underline">
                {shareUrl.replace(/^https:\/\//, "")}
              </a>
              <Button
                size="sm"
                variant="outline"
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(shareUrl);
                    setCopied(true);
                  } catch {
                    setCopied(false);
                  }
                }}
              >
                {copied ? "已複製" : "複製連結"}
              </Button>
            </p>
          )}
          <Button
            variant="outline"
            onClick={() => {
              setValue(handle ?? "");
              setTouched(false);
              save.reset();
              setEditing(true);
            }}
          >
            {handle ? "修改 @handle" : "設定 @handle"}
          </Button>
        </div>
      ) : (
        <div className="space-y-2">
          <Label htmlFor="artist-profile-handle">@handle（{HANDLE_RULE_TEXT}；工作人員可隨時修改，藝人在 App 只能設定一次）</Label>
          <div className="flex max-w-sm items-center gap-1">
            <span aria-hidden className="text-muted-foreground">
              @
            </span>
            <Input
              id="artist-profile-handle"
              value={value}
              maxLength={40}
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              aria-invalid={touched && !!problem}
              disabled={save.isPending}
              onChange={(e) => setValue(e.target.value)}
              onBlur={() => setTouched(true)}
            />
          </div>
          {!problem && <p className="text-xs text-muted-foreground break-all">分享連結將是 {handleShareUrl(value).replace(/^https:\/\//, "")}</p>}
          {touched && problem && <p className="text-sm text-destructive">{problem}</p>}
          {handle && !problem && normalizeHandle(value) !== handle && (
            <p className="text-xs text-amber-700">修改後舊連結 on-reel.com/a/{handle} 可能失效，已分享出去的連結需更新。</p>
          )}
          {save.isError && (
            <p role="alert" className="text-sm text-destructive">
              {save.error instanceof Error ? save.error.message : "操作未完成，請重試"}
            </p>
          )}
          <div className="flex flex-wrap gap-2">
            <Button
              disabled={save.isPending || !!problem || normalizeHandle(value) === handle}
              onClick={() => {
                setTouched(true);
                if (!problem) save.mutate();
              }}
            >
              {save.isPending ? "儲存中…" : "儲存 @handle"}
            </Button>
            <Button variant="outline" disabled={save.isPending} onClick={() => setEditing(false)}>
              取消
            </Button>
          </div>
        </div>
      )}
      {save.isSuccess && !editing && <p role="status" className="text-sm">@handle 已更新。</p>}
    </section>
  );
}
